import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Stack,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Pagination,
} from "@mui/material";

import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import BlockIcon from "@mui/icons-material/Block";
import RestoreIcon from "@mui/icons-material/Restore";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonIcon from "@mui/icons-material/Person";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

import api from "../../utils/api";

const PAGE_SIZE = 10;

export default function AdminManageUsers() {
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);

  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [verified, setVerified] = useState("");

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [toast, setToast] = useState({
    open: false,
    msg: "",
    severity: "info",
  });

  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [actionType, setActionType] = useState("");

  const notify = (msg, severity = "info") =>
    setToast({ open: true, msg, severity });

  // ======================================================
  // LOAD USERS
  // ======================================================
  const load = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/users", {
        params: {
          search,
          role,
          status,
          verified,
          page,
          limit: PAGE_SIZE,
        },
      });

      setUsers(res.data.users || []);
      setTotal(res.data.pagination.total);
      setTotalPages(res.data.pagination.totalPages);
    } catch {
      notify("Failed to load users", "error");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, [search, role, status, verified, page]);

  const openModal = (user, action) => {
    setSelectedUser(user);
    setActionType(action);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  // ======================================================
  // ADMIN ACTIONS
  // ======================================================
  const doAction = async () => {
    if (!selectedUser) return;

    try {
      switch (actionType) {
        case "verify":
          await api.patch(`/admin/users/${selectedUser.id}/verify`);
          notify("User email verified", "success");
          break;

        case "activate":
          await api.patch(`/admin/users/${selectedUser.id}/status`, {
            status: "active",
          });
          notify("User activated", "success");
          break;

        case "deactivate":
          await api.patch(`/admin/users/${selectedUser.id}/status`, {
            status: "inactive",
          });
          notify("User deactivated", "warning");
          break;

        case "promote":
          await api.patch(`/admin/users/${selectedUser.id}/role`, {
            role: "admin",
          });
          notify("User promoted to admin", "success");
          break;

        case "demote":
          await api.patch(`/admin/users/${selectedUser.id}/role`, {
            role: "student",
          });
          notify("User demoted to student", "warning");
          break;

        case "promote_agent": {
          const res = await api.post("/admin/agents/upgrade", {
            user_id: selectedUser.id,
          });
          notify(
            `User promoted to Agent. Referral Code: ${res.data.referral_code}`,
            "success"
          );
          break;
        }

        case "demote_agent":
          await api.post("/admin/agents/demote", {
            user_id: selectedUser.id,
          });
          notify(
            "Agent demoted to student. Referral history preserved.",
            "warning"
          );
          break;

        case "reset": {
          const res = await api.post(
            `/admin/users/${selectedUser.id}/reset-password`
          );
          notify(`Temp password: ${res.data.tempPassword}`, "success");
          break;
        }

        case "delete":
          await api.delete(`/admin/users/${selectedUser.id}`);
          notify("User deleted permanently", "success");
          break;

        default:
          break;
      }

      closeModal();
      load();
    } catch (err) {
      notify(
        err?.response?.data?.message || "Action failed",
        "error"
      );
    }
  };

  const startIndex = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, total);

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
        Manage Users
      </Typography>

      {/* FILTERS */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }} variant="outlined">
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField
            label="Search"
            size="small"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            fullWidth
          />

          <TextField
            label="Role"
            select
            size="small"
            value={role}
            onChange={(e) => {
              setPage(1);
              setRole(e.target.value);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="student">Student</MenuItem>
            <MenuItem value="agent">Agent</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </TextField>

          <TextField
            label="Status"
            select
            size="small"
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>

          <TextField
            label="Verification"
            select
            size="small"
            value={verified}
            onChange={(e) => {
              setPage(1);
              setVerified(e.target.value);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="verified">Verified</MenuItem>
            <MenuItem value="unverified">Unverified</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      {/* TABLE */}
      <Paper variant="outlined" sx={{ borderRadius: 3 }}>
        {busy ? (
          <Stack alignItems="center" sx={{ p: 3 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <TableContainer sx={{ maxHeight: "70vh" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Verified</TableCell>
                  <TableCell>Registered</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((u) => (
                    <TableRow key={u.id} hover>
                      <TableCell>{u.full_name}</TableCell>
                      <TableCell>{u.email}</TableCell>

                      <TableCell>
                        <Chip
                          label={u.role}
                          size="small"
                          color={
                            u.role === "admin"
                              ? "primary"
                              : u.role === "agent"
                              ? "success"
                              : "default"
                          }
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={u.status}
                          size="small"
                          color={
                            u.status === "active"
                              ? "success"
                              : "warning"
                          }
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={u.is_verified ? "Verified" : "Unverified"}
                          size="small"
                          color={u.is_verified ? "success" : "warning"}
                        />
                      </TableCell>

                      <TableCell>
                        {new Date(u.created_at).toLocaleDateString()}
                      </TableCell>

                      <TableCell align="right">
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {!u.is_verified && (
                            <IconButton
                              color="success"
                              onClick={() => openModal(u, "verify")}
                            >
                              <VerifiedUserIcon />
                            </IconButton>
                          )}

                          {u.status === "active" ? (
                            <IconButton
                              color="warning"
                              onClick={() => openModal(u, "deactivate")}
                            >
                              <BlockIcon />
                            </IconButton>
                          ) : (
                            <IconButton
                              color="success"
                              onClick={() => openModal(u, "activate")}
                            >
                              <RestoreIcon />
                            </IconButton>
                          )}

                          {u.role === "student" && (
                            <>
                              <IconButton
                                color="primary"
                                title="Promote to Admin"
                                onClick={() => openModal(u, "promote")}
                              >
                                <AdminPanelSettingsIcon />
                              </IconButton>

                              <IconButton
                                color="secondary"
                                title="Promote to Agent"
                                onClick={() => openModal(u, "promote_agent")}
                              >
                                <SupportAgentIcon />
                              </IconButton>
                            </>
                          )}

                          {u.role === "agent" && (
                            <IconButton
                              color="warning"
                              title="Demote Agent to Student"
                              onClick={() => openModal(u, "demote_agent")}
                            >
                              <PersonIcon />
                            </IconButton>
                          )}

                          {u.role === "admin" && (
                            <IconButton
                              color="warning"
                              title="Demote Admin to Student"
                              onClick={() => openModal(u, "demote")}
                            >
                              <PersonIcon />
                            </IconButton>
                          )}

                          <IconButton
                            color="info"
                            onClick={() => openModal(u, "reset")}
                          >
                            <RestartAltIcon />
                          </IconButton>

                        
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* PAGINATION */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 2 }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {startIndex}–{endIndex} of {total} users
          </Typography>

          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, v) => setPage(v)}
            showFirstButton
            showLastButton
          />
        </Stack>
      </Paper>

      {/* CONFIRM MODAL */}
      <Dialog open={modalOpen} onClose={closeModal}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to <b>{actionType}</b> user{" "}
            <b>{selectedUser?.full_name}</b>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Cancel</Button>
          <Button
            variant="contained"
            color={actionType === "delete" ? "error" : "primary"}
            onClick={doAction}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* TOAST */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
      >
        <Alert severity={toast.severity} variant="filled">
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
